import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ContainerInfo {
  containerName: string;
  containerVersion: string;
  containerRegistry?: string;
}

export type ChangelogEnvironment = 'connectPob' | 'connect';

export const CHANGELOG_ENVIRONMENTS: { value: ChangelogEnvironment; label: string }[] = [
  { value: 'connect', label: 'Connect' },
  { value: 'connectPob', label: 'Connect POB' },
];

export interface ReleaseChangelog {
  releaseVersion: string;
  heading: string | null;
  title: string;
  htmlContent: string;
  linkToManual: string;
  environment: ChangelogEnvironment[];
  /** Base64-encoded image bytes (no data: prefix), or null when no image is set. */
  image: string | null;
}

export interface IotEdgeReleaseResponse {
  id: string;
  version: string;
  lastUpdatedAt: string;
  releaseDate: string | null;
  containers: ContainerInfo[];
  changelog: ReleaseChangelog | null;
  readySiteIds: string[];
}

/** How a site got its desired release. The API serializes enums as camelCase strings. */
export enum ReleaseTrigger {
  /** An admin forced the release from the cloud. */
  Desired = 'desired',
  /** The gateway picked the release itself out of the ones made available to it (auto-deploy). */
  LatestAvailable = 'latestAvailable',
}

export enum DeploymentHealthStatus {
  Unknown = 0,
  Healthy = 1,
  ReportingFailure = 2,
  ContainerMismatch = 3,
}

export interface SiteReleasesResponse {
  currentRelease: IotEdgeReleaseResponse | null;
  desiredRelease: IotEdgeReleaseResponse | null;
  availableReleases: IotEdgeReleaseResponse[];
  deploymentStatus: DeploymentHealthStatus | string;
  missingContainers: ContainerInfo[] | null;
  deploymentHealthCheckedAt: string | null;
  reportedContainers: ContainerInfo[] | null;
  reportedContainersAt: string | null;
  reportedMissingContainers: ContainerInfo[] | null;
  releaseTrigger: ReleaseTrigger | null;
  releasedAt: string | null;
}

export interface SetDesiredReleaseRequest {
  releaseId: string;
}

export interface SetAvailableReleasesRequest {
  availableReleaseIds: string[];
}

export interface SetChangelogRequest {
  changelog: ReleaseChangelog | null;
}

@Injectable()
export class IotEdgeReleaseService {
  constructor(private httpClient: HttpClient) {}

  private readonly releaseApiAddress = environment.apiAddress + '/IotEdgeRelease';
  private readonly siteApiAddress = environment.apiAddress + '/Site';

  getAllReleases(): Observable<IotEdgeReleaseResponse[]> {
    return this.httpClient.get<IotEdgeReleaseResponse[]>(this.releaseApiAddress);
  }

  syncReleases(): Observable<void> {
    return this.httpClient.post<void>(this.releaseApiAddress + '/sync', {});
  }

  setChangelog(releaseId: string, request: SetChangelogRequest): Observable<void> {
    return this.httpClient.put<void>(`${this.releaseApiAddress}/${releaseId}/changelog`, request);
  }

  getSiteReleases(siteId: string): Observable<SiteReleasesResponse> {
    return this.httpClient.get<SiteReleasesResponse>(`${this.siteApiAddress}/${siteId}/releases`);
  }

  setDesiredRelease(siteId: string, releaseId: string): Observable<SiteReleasesResponse> {
    return this.httpClient.put<SiteReleasesResponse>(`${this.siteApiAddress}/${siteId}/releases/desired`, {
      releaseId,
    } as SetDesiredReleaseRequest);
  }

  setAvailableReleases(siteId: string, availableReleaseIds: string[]): Observable<SiteReleasesResponse> {
    return this.httpClient.put<SiteReleasesResponse>(`${this.siteApiAddress}/${siteId}/releases/available`, {
      availableReleaseIds,
    } as SetAvailableReleasesRequest);
  }

  putSiteReleases(siteId: string, model: any): Observable<any> {
    return this.httpClient.put(`${this.siteApiAddress}/${siteId}/releases`, model);
  }
}
